import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Grid, Typography, TextField, IconButton, Button, Select, MenuItem, FormControl, InputAdornment, Box, CircularProgress } from '@mui/material';
import { DesktopDatePicker } from '@mui/lab';
import { Close, Save, AttachFile, Delete, Download } from '@mui/icons-material';
import { useDropzone, FileRejection } from 'react-dropzone';
import { IPrepaidDealCost, IPrepaidDealCostFile, AdvancesFinancialStatus } from '../../../../models/agent-draws-and-deductions/prepaid-deal-cost';
import { PrepaidDealCostsAPI } from '../../../../fetch/prepaid-deal-costs';
import { downloadFile as downloadFileSave, getDownloadFileName } from '../../../../helpers';
import { useToastContext } from '../../../../hooks';

const MAX_FILE_SIZE = 10 * 1024 * 1024;

interface AddPayload {
  date: string;
  amount: number;
  fsReimbursementPercent: number;
  memo?: string;
  notes?: string;
}

interface EditPayload extends AddPayload {
  advancesFinancialStatusId?: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  initialData?: IPrepaidDealCost | null;
  statuses: { advancesFinancialStatus: AdvancesFinancialStatus[] } | null;
  employeeName: string;
  onCreate: (payload: AddPayload) => Promise<number | void>;
  onEditSave: (id: number, payload: EditPayload) => Promise<void>;
  saving?: boolean;
  onFileUploaded?: () => void;
}

const denseLabelStyle: React.CSSProperties = {
  fontWeight: 700,
  fontSize: '0.85rem',
  color: '#222',
  display: 'flex',
  alignItems: 'center',
  height: '100%'
};

// Stable date helper functions (module scope)
const toYmdLocal = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};
const todayLocalYmd = () => toYmdLocal(new Date());
const parseYmdToLocalDate = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

export const AddEditPrepaidDealCostModal: React.FC<Props> = ({
  open,
  onClose,
  initialData,
  statuses,
  employeeName,
  onCreate,
  onEditSave,
  saving = false,
  onFileUploaded
}) => {
  const isEdit = useMemo(() => !!initialData, [initialData]);

  const [date, setDate] = useState<string>(todayLocalYmd());
  const [amount, setAmount] = useState<string>('');
  const [fsReimbursementPercent, setFsReimbursementPercent] = useState<string>('');
  const [memo, setMemo] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [advancesFinancialStatusId, setAdvancesFinancialStatusId] = useState<string>('');
  
  // File upload state
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [existingFiles, setExistingFiles] = useState<IPrepaidDealCostFile[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [downloadingFileId, setDownloadingFileId] = useState<number | null>(null);
  
  const [isSaving, setIsSaving] = useState(false);

  // Upload a single file immediately (for edit mode)
  const uploadFileImmediately = useCallback(async (file: File) => {
    if (!initialData?.id) return;
    setUploadingFiles(true);
    try {
      await PrepaidDealCostsAPI.uploadFile(initialData.id, file);
      onFileUploaded?.(); 
    } catch (error) {
      console.error('Failed to upload file:', error);
    } finally {
      setUploadingFiles(false);
    }
  }, [initialData?.id, onFileUploaded]);

  // Handle file drop
  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (isEdit && initialData?.id) {
      acceptedFiles.forEach(file => uploadFileImmediately(file));
    } else {
      // Create mode: queue files for upload after save
      setPendingFiles(prev => [...prev, ...acceptedFiles]);
    }
  }, [isEdit, initialData?.id, uploadFileImmediately]);

  const { showToast } = useToastContext();

  const onDropRejected = useCallback((rejectedFiles: FileRejection[]) => {
    rejectedFiles.forEach(({ file, errors }) => {
      const sizeError = errors.find(e => e.code === 'file-too-large');
      const typeError = errors.find(e => e.code === 'file-invalid-type');
      
      if (sizeError) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        showToast('error', `File "${file.name}" is too large (${sizeMB}MB). Maximum size is 10MB.`);
      } else if (typeError) {
        showToast('error', `File "${file.name}" is not a supported type. Only PDF and images are allowed.`);
      }
    });
  }, [showToast]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp']
    }
  });

  // Remove pending file
  const removePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Download existing file
  const handleDownloadFile = async (file: IPrepaidDealCostFile) => {
    if (!initialData?.id) return;
    setDownloadingFileId(file.id);
    try {
      const response = await PrepaidDealCostsAPI.downloadFile(initialData.id, file.id);
      const fileName = getDownloadFileName(response) || file.fileName;
      downloadFileSave(response.data, fileName);
    } catch (error) {
      console.error('Failed to download file:', error);
    } finally {
      setDownloadingFileId(null);
    }
  };

  const sanitizeAmountInput = (raw: string) => {
    let v = raw || '';
    // digits, dot and minus
    v = v.replace(/[^0-9.-]/g, '');
    // keep minus only once and only at the start
    const isNeg = v.includes('-');
    v = v.replace(/-/g, '');
    if (isNeg) v = '-' + v;
    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      const head = v.slice(0, firstDot + 1);
      let tail = v.slice(firstDot + 1).replace(/\./g, '');
      
      tail = tail.slice(0, 3);
      v = head + tail;
    }
    return v;
  };

  // Sanitize percentage input
  const sanitizePercentInput = (raw: string) => {
    let v = raw || '';
    v = v.replace(/[^0-9.]/g, '');
    const firstDot = v.indexOf('.');
    if (firstDot !== -1) {
      const head = v.slice(0, firstDot + 1);
      let tail = v.slice(firstDot + 1).replace(/\./g, '');
      
      tail = tail.slice(0, 3);
      v = head + tail;
    }
    return v;
  };

  // Calculate remaining amount (Due from Agent)
  const remainingAmount = useMemo(() => {
    const amountNum = parseFloat(amount) || 0;
    const percentNum = parseFloat(fsReimbursementPercent) || 0;
    // FS Portion = Amount * (FS Reimbursement % / 100)
    // Due from Agent (Remaining) = Amount - FS Portion
    const fsPortion = amountNum * (percentNum / 100);
    const dueFromAgent = amountNum - fsPortion;
    return dueFromAgent;
  }, [amount, fsReimbursementPercent]);

  const formatCurrency = (value: number) => {
    if (isNaN(value)) return '$0.00';
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    return `${sign}$${abs.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}`;
  };

  useEffect(() => {
    if (!open) return;
    if (isEdit && initialData) {
      if (initialData.createdDateString) {
        const d = new Date(initialData.createdDateString);
        if (!isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          setDate(`${y}-${m}-${dd}`);
        }
      }
      setAmount(initialData.amount !== undefined ? String(initialData.amount) : '');
      setFsReimbursementPercent(initialData.fsReimbursementPercent !== undefined ? String(initialData.fsReimbursementPercent) : '');
      setMemo(initialData.memo ?? '');
      setNotes(initialData.notes ?? '');
      setAdvancesFinancialStatusId(initialData.advancesFinancialStatusId ? String(initialData.advancesFinancialStatusId) : '');
      setExistingFiles(initialData.files ?? []);
      setPendingFiles([]);
    } else {
      setDate(todayLocalYmd());
      setAmount('');
      setFsReimbursementPercent('');
      setMemo('');
      setNotes('');
      setAdvancesFinancialStatusId('');
      setExistingFiles([]);
      setPendingFiles([]);
    }
  }, [open, isEdit, initialData]);

  // Upload pending files for an agent deduction (backend handles filename generation)
  const uploadPendingFiles = async (agentDeductionId: number) => {
    if (pendingFiles.length === 0) return;
    setUploadingFiles(true);
    try {
      for (const file of pendingFiles) {
        await PrepaidDealCostsAPI.uploadFile(agentDeductionId, file);
      }
      onFileUploaded?.();
    } finally {
      setUploadingFiles(false);
    }
  };

  const handleSave = async () => {
    const amountNum = Number(amount);
    const percentNum = Number(fsReimbursementPercent);
    
    if (isNaN(amountNum)) return;
    if (isNaN(percentNum) || percentNum < 0 || percentNum > 100) return;

    setIsSaving(true);
    try {
      if (isEdit && initialData?.id) {
        await onEditSave(initialData.id, {
          date,
          amount: amountNum,
          fsReimbursementPercent: percentNum,
          memo,
          notes,
          advancesFinancialStatusId: advancesFinancialStatusId ? Number(advancesFinancialStatusId) : undefined
        });
        onFileUploaded?.();
        return;
      }
      
      // Create new record and get the ID back
      const result = await onCreate({
        date,
        amount: amountNum,
        fsReimbursementPercent: percentNum,
        memo,
        notes
      });

      // If create returned an ID and there are pending files, upload first
      if (typeof result === 'number' && pendingFiles.length > 0) {
        setIsSaving(false); 
        await uploadPendingFiles(result);
      } else if (typeof result === 'number') {
        
        onFileUploaded?.();
      }
    } finally {
      setIsSaving(false);
    }
  };

  const financialStatuses = useMemo(() => (statuses?.advancesFinancialStatus ?? []), [statuses]);

  const modalTitle = `Pre Paid Deal Costs${employeeName ? ` - ${employeeName}` : ''}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{
        '& .MuiDialog-paper': {
          width: '720px',
          maxWidth: 'calc(100% - 48px)',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, px: 1.5, bgcolor: '#005570', color: '#eff4f6' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
          {isEdit ? 'Edit Pre Paid Deal Cost' : modalTitle}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: '#eff4f6' }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2, pt: 3, pb: 1, overflowX: 'hidden' }}>
        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          {/* Date */}
          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>Date</Typography>
          </Grid>
          <Grid item xs={7}>
            <DesktopDatePicker
              inputFormat="M/d/yyyy"
              value={date ? parseYmdToLocalDate(date) : null}
              onChange={(d) => {
                if (d instanceof Date && !isNaN(d.getTime())) {
                  setDate(toYmdLocal(d));
                }
              }}
              renderInput={(params) => (
                <TextField {...params} name="date" fullWidth size="small" />
              )}
            />
          </Grid>

          {/* Amount */}
          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>Amount</Typography>
          </Grid>
          <Grid item xs={7}>
            <TextField
              fullWidth
              name="amount"
              value={amount}
              onChange={(e) => setAmount(sanitizeAmountInput(e.target.value))}
              size="small"
              placeholder="e.g. -5000 or 2500.000"
              autoComplete="off"
              inputMode="decimal"
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                },
                '& .MuiOutlinedInput-root.Mui-focused': {
                  bgcolor: 'background.paper',
                },
                '& input': {
                  backgroundColor: 'transparent !important',
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                  WebkitTextFillColor: 'inherit',
                  caretColor: 'inherit',
                },
              }}
            />
          </Grid>

          {/* FS Reimbursement % */}
          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>FS Reimbursement %</Typography>
          </Grid>
          <Grid item xs={7}>
            <TextField
              fullWidth
              name="fsReimbursementPercent"
              value={fsReimbursementPercent}
              onChange={(e) => setFsReimbursementPercent(sanitizePercentInput(e.target.value))}
              size="small"
              placeholder="e.g. 50 or 33.333"
              autoComplete="off"
              inputMode="decimal"
              InputProps={{
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                },
                '& .MuiOutlinedInput-root.Mui-focused': {
                  bgcolor: 'background.paper',
                },
                '& input': {
                  backgroundColor: 'transparent !important',
                },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                  WebkitTextFillColor: 'inherit',
                  caretColor: 'inherit',
                },
              }}
            />
          </Grid>

          {/* Remaining Amount (auto-calculated) */}
          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>Remaining Amount</Typography>
          </Grid>
          <Grid item xs={7}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: 40,
                px: 1.5,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {formatCurrency(remainingAmount)}
              </Typography>
            </Box>
          </Grid>

          {/* Files */}
          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>Files (Max 10MB)</Typography>
          </Grid>
          <Grid item xs={7}>
            {/* Dropzone */}
            <Box
              {...getRootProps()}
              sx={{
                border: isDragActive ? '2px solid #005570' : '2px dashed #ccc',
                borderRadius: 1,
                p: 2,
                textAlign: 'center',
                cursor: 'pointer',
                bgcolor: isDragActive ? '#e3f2fd' : '#fafafa',
                '&:hover': {
                  bgcolor: '#f0f0f0',
                  borderColor: '#999',
                },
                mb: 1,
              }}
            >
              <input {...getInputProps()} />
              <AttachFile sx={{ color: '#666', mb: 0.5 }} />
              <Typography variant="caption" color="text.secondary" display="block">
                {isDragActive ? 'Drop files here...' : 'Drag and drop files here or click to upload'}
              </Typography>
            </Box>

            {/* Pending files (not yet uploaded) */}
            {pendingFiles.length > 0 && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Files to upload:
                </Typography>
                {pendingFiles.map((file, index) => (
                  <Box
                    key={`pending-${index}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      px: 1,
                      bgcolor: '#fff8e1',
                      borderRadius: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {file.name}
                    </Typography>
                    <IconButton size="small" onClick={() => removePendingFile(index)} sx={{ ml: 1 }}>
                      <Delete fontSize="small" color="error" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            {/* Existing files (already uploaded) */}
            {existingFiles.length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Attached files:
                </Typography>
                {existingFiles.map((file) => (
                  <Box
                    key={`existing-${file.id}`}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      py: 0.5,
                      px: 1,
                      bgcolor: '#e8f5e9',
                      borderRadius: 0.5,
                      mt: 0.5,
                    }}
                  >
                    <Typography variant="caption" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {file.fileName}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadFile(file)}
                      disabled={downloadingFileId === file.id}
                      sx={{ ml: 1 }}
                    >
                      {downloadingFileId === file.id ? (
                        <CircularProgress size={16} />
                      ) : (
                        <Download fontSize="small" color="primary" />
                      )}
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Grid>

          {/* Memo */}
          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>Memo</Typography>
          </Grid>
          <Grid item xs={7}>
            <TextField
              fullWidth
              name="memo"
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              size="small"
              multiline
              minRows={3}
            />
          </Grid>

          {/* Financial Status */}
          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>Financial Status</Typography>
          </Grid>
          <Grid item xs={7}>
            <FormControl fullWidth size="small">
              <Select
                name="advancesFinancialStatusId"
                value={advancesFinancialStatusId}
                onChange={(e) => setAdvancesFinancialStatusId(String(e.target.value))}
                displayEmpty
                disabled={!isEdit}
              >
                <MenuItem value="">
                  <em>Select status</em>
                </MenuItem>
                {financialStatuses.map((s: AdvancesFinancialStatus) => (
                  <MenuItem key={s.value} value={s.value}>{s.description}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Notes */}
          <Grid item xs={5}>
            <Typography sx={denseLabelStyle}>Notes</Typography>
          </Grid>
          <Grid item xs={7}>
            <TextField
              fullWidth
              name="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              size="small"
              multiline
              minRows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          startIcon={<Close fontSize="small" />}
          onClick={onClose}
          disabled={isSaving || uploadingFiles}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="secondary"
          startIcon={isSaving || uploadingFiles ? <CircularProgress size={16} color="inherit" /> : <Save fontSize="small" />}
          onClick={handleSave}
          disabled={isSaving || uploadingFiles || isNaN(Number(amount))}
        >
          {uploadingFiles ? 'Uploading files...' : isSaving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEditPrepaidDealCostModal;
